// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { usePermissionChecker } from '@/authorization/hooks/use-user-policy';
import { Permission } from '@/authorization/types';
import { PrimaryMetric, SummaryCard } from '@/components/summary-card';
import { TimeRecordFilter } from '@/costs/api/get-time-records';
import { useTimeRecordAggregate } from '@/costs/hooks/use-time-record-aggregate';
import { formatHours } from '@/costs/utilities/format-hours';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { Box, Grid, LinearProgress, Skeleton } from '@mui/material';
import { Fragment } from 'react';

/**
 * Props for the TimeRecordsSummary component
 */
interface Props {
  /**
   * Filter criteria to determine which time records to include in the summary.
   */
  filter?: Omit<TimeRecordFilter, 'pageSize' | 'pageNumber' | 'sort'>;
}

/**
 * Skeleton component for loading state
 */
function TimeRecordsSummarySkeleton() {
  return (
    <Box sx={{ marginBottom: 2 }}>
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map((index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <SummaryCard>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="80%" height={48} />
            </SummaryCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

/**
 * A component that displays time records summary aggregated across filtered time entries
 */
export function TimeRecordsSummary(props: Props) {
  const { aggregateData, isLoading, isFetching } = useTimeRecordAggregate(
    props.filter
  );
  const { hasPermission } = usePermissionChecker();

  // Check if user has permission to view time rates
  const canViewRates =
    hasPermission('time.rate', 'read') === Permission.PermissionExplicitTrue ||
    hasPermission('time.rate', 'read') === Permission.PermissionImplicitTrue;

  if (isLoading) {
    return <TimeRecordsSummarySkeleton />;
  }

  const totalEntries = aggregateData.totalEntries;

  // Without rate visibility the currency split isn't meaningful, so keep the
  // simple cross-currency grand totals.
  if (!canViewRates) {
    return (
      <Box sx={{ marginBottom: 1 }}>
        {isFetching && <LinearProgress />}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={6}>
            <SummaryCard>
              <PrimaryMetric
                label="Total hours"
                value={
                  totalEntries > 0
                    ? formatHours(aggregateData.totalMinutes)
                    : '—'
                }
              />
            </SummaryCard>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <SummaryCard>
              <PrimaryMetric
                label="Total time entries"
                value={totalEntries > 0 ? totalEntries.toLocaleString() : '—'}
              />
            </SummaryCard>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Empty state: no entries at all.
  if (totalEntries === 0) {
    return (
      <Box sx={{ marginBottom: 1 }}>
        {isFetching && <LinearProgress />}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard>
              <PrimaryMetric label="Total hours" value="—" />
            </SummaryCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard>
              <PrimaryMetric label="Total time entries" value="—" />
            </SummaryCard>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Entries with no rate carry no currency, so they live outside byCurrency; the
  // remainder of the grand totals is their hours and entry count.
  const ratedMinutes = aggregateData.byCurrency.reduce(
    (sum, bucket) => sum + bucket.totalMinutes,
    0
  );
  const ratedEntries = aggregateData.byCurrency.reduce(
    (sum, bucket) => sum + bucket.totalEntries,
    0
  );
  const noRateMinutes = aggregateData.totalMinutes - ratedMinutes;
  const noRateEntries = totalEntries - ratedEntries;
  const hasNoRate = noRateEntries > 0;

  // Only qualify the hours/entries labels with a currency when more than one
  // group is shown; a single group needs no disambiguation.
  const groupCount = aggregateData.byCurrency.length + (hasNoRate ? 1 : 0);
  const hoursLabelSuffix = (suffix: string) =>
    groupCount > 1 ? ` (${suffix})` : '';

  return (
    <Box sx={{ marginBottom: 1 }}>
      {isFetching && <LinearProgress />}
      <Grid container spacing={3}>
        {aggregateData.byCurrency.map((bucket) => (
          <Fragment key={bucket.isoCode}>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard>
                <PrimaryMetric
                  label={`Total hours${hoursLabelSuffix(bucket.isoCode)}`}
                  value={formatHours(bucket.totalMinutes)}
                />
              </SummaryCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard>
                <PrimaryMetric
                  label={`Total time entries${hoursLabelSuffix(bucket.isoCode)}`}
                  value={bucket.totalEntries.toLocaleString()}
                />
              </SummaryCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard>
                <PrimaryMetric
                  label={`Total value (${bucket.isoCode})`}
                  value={formatAmountWithCurrency(
                    bucket.totalValue,
                    bucket.isoCode
                  )}
                />
              </SummaryCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard>
                <PrimaryMetric
                  label={`Average hourly rate (${bucket.isoCode})`}
                  value={formatAmountWithCurrency(
                    bucket.averageHourlyRate,
                    bucket.isoCode
                  )}
                />
              </SummaryCard>
            </Grid>
          </Fragment>
        ))}
        {hasNoRate && (
          <Fragment key="no-rate">
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard>
                <PrimaryMetric
                  label={`Total hours${hoursLabelSuffix('No rate')}`}
                  value={formatHours(noRateMinutes)}
                />
              </SummaryCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard>
                <PrimaryMetric
                  label={`Total time entries${hoursLabelSuffix('No rate')}`}
                  value={noRateEntries.toLocaleString()}
                />
              </SummaryCard>
            </Grid>
          </Fragment>
        )}
      </Grid>
    </Box>
  );
}
